// src/pages/admin/ProductCategoriesManage.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Typography, Modal, Form, Select } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const ProductCategoriesManage = () => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState(null);
  const [form] = Form.useForm();

  // Simulated data for categories and products (replace with API calls)
  const mockCategories = [
    { id: 1, name: 'AI' },
    { id: 2, name: 'Web Development' },
    { id: 3, name: 'Cybersecurity' },
  ];
  const mockProducts = [
    { id: 1, name: 'AI Toolkit' },
    { id: 2, name: 'Web Builder Pro' },
    { id: 3, name: 'Security Suite' },
  ];

  // Simulated API call to fetch product_categories
  const fetchProductCategories = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const mockData = [
        { category_id: 1, product_id: 1, category_name: 'AI', product_name: 'AI Toolkit' },
        { category_id: 2, product_id: 2, category_name: 'Web Development', product_name: 'Web Builder Pro' },
        { category_id: 3, product_id: 3, category_name: 'Cybersecurity', product_name: 'Security Suite' },
        // Add more mock data as needed
      ].filter(pc =>
        pc.category_name.toLowerCase().includes(search.toLowerCase()) ||
        pc.product_name.toLowerCase().includes(search.toLowerCase())
      );

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = mockData.slice(start, end);

      setProductCategories(paginatedData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: mockData.length,
      });
    } catch (error) {
      message.error('Failed to fetch product categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductCategories(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  const columns = [
    { title: 'Category ID', dataIndex: 'category_id', key: 'category_id', sorter: (a, b) => a.category_id - b.category_id },
    { title: 'Category Name', dataIndex: 'category_name', key: 'category_name', sorter: (a, b) => a.category_name.localeCompare(b.category_name) },
    { title: 'Product ID', dataIndex: 'product_id', key: 'product_id', sorter: (a, b) => a.product_id - b.product_id },
    { title: 'Product Name', dataIndex: 'product_name', key: 'product_name', sorter: (a, b) => a.product_name.localeCompare(b.product_name) },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="Are you sure to delete this product-category association?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleView = (productCategory) => {
    setSelectedProductCategory(productCategory);
    setViewModalVisible(true);
  };

  const handleEdit = (productCategory) => {
    setSelectedProductCategory(productCategory);
    form.setFieldsValue(productCategory); // Pre-fill form with product-category data
    setEditModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      const updatedProductCategories = productCategories.filter(pc => 
        !(pc.category_id === record.category_id && pc.product_id === record.product_id)
      );
      setProductCategories(updatedProductCategories);
      message.success('Product-category association deleted successfully');
      fetchProductCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to delete product-category association');
      console.error(error);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchProductCategories(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleCreateSubmit = async (values) => {
    try {
      const newProductCategory = {
        ...values,
        category_name: mockCategories.find(cat => cat.id === values.category_id)?.name || 'Unknown',
        product_name: mockProducts.find(prod => prod.id === values.product_id)?.name || 'Unknown',
      };
      setProductCategories([...productCategories, newProductCategory]);
      message.success('Product-category association created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchProductCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to create product-category association');
      console.error(error);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      const updatedProductCategories = productCategories.map(pc =>
        pc.category_id === selectedProductCategory.category_id && pc.product_id === selectedProductCategory.product_id
          ? {
              ...pc,
              ...values,
              category_name: mockCategories.find(cat => cat.id === values.category_id)?.name || 'Unknown',
              product_name: mockProducts.find(prod => prod.id === values.product_id)?.name || 'Unknown',
            }
          : pc
      );
      setProductCategories(updatedProductCategories);
      message.success('Product-category association updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchProductCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to update product-category association');
      console.error(error);
    }
  };

  const productCategoryFormFields = (
    <>
      <Form.Item
        name="category_id"
        label="Category"
        rules={[{ required: true, message: 'Please select a category' }]}
      >
        <Select placeholder="Select a category">
          {mockCategories.map(category => (
            <Option key={category.id} value={category.id}>{category.name}</Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="product_id"
        label="Product"
        rules={[{ required: true, message: 'Please select a product' }]}
      >
        <Select placeholder="Select a product">
          {mockProducts.map(product => (
            <Option key={product.id} value={product.id}>{product.name}</Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ fontSize: '15px' }}>Manage Products Categories</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input.Search
            placeholder="Search by category or product name"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add New Association
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={productCategories}
        rowKey={record => `${record.category_id}-${record.product_id}`} // Composite key
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} associations`,
        }}
        onChange={handleTableChange}
      />

      {/* View Modal */}
      <Modal
        title="Product-Category Association Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={<Button onClick={() => setViewModalVisible(false)}>Close</Button>}
        width={600}
      >
        {selectedProductCategory && (
          <div>
            <p><strong>Category ID:</strong> {selectedProductCategory.category_id}</p>
            <p><strong>Category Name:</strong> {selectedProductCategory.category_name}</p>
            <p><strong>Product ID:</strong> {selectedProductCategory.product_id}</p>
            <p><strong>Product Name:</strong> {selectedProductCategory.product_name}</p>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        title="Add New Product-Category Association"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {productCategoryFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Create Association</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Product-Category Association"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          {productCategoryFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Update Association</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductCategoriesManage;